/**
 * Which steps of the weekly checkup a given person actually walks through.
 *
 * The wizard hides steps for some people. The body-fat step is hidden when we
 * already have a recent body-fat measurement, and the nutrition-logging and
 * calorie-baseline steps are hidden when the app cannot tell how the person's
 * weight is trending. Everything the page needs to know about navigation — the
 * step counter, its total, where Back goes and where Next goes — is an answer
 * about that one list, so this module derives the list once and answers all four
 * questions from it.
 *
 * This used to be four separate rules written four different ways inside
 * `weekly-checkup-page.ts` (deferred finding `df-d6c9030294e9`, internal ticket
 * https://github.com/cybsol/fitness-app/issues/433). Two of them were arithmetic
 * on the raw step number and two were step-to-step rewrites, and nothing linked
 * them: every one operates on the same enum, so every wrong combination still
 * type-checks. That is not a hypothetical cost. A fifth site, the body-fat Skip
 * button, had already drifted out of step with the other four, and the symptom
 * was a person being shown a step the rest of the page hides under a counter
 * reading "Step 4 of 4" twice in a row (`df-8aeb1529c055`, ticket 180).
 *
 * The module deliberately has no Angular dependency, so a unit test can drive it
 * (`weekly-checkup-steps.test.ts`). The page keeps owning the two conditions and
 * passes them in fresh on every call, because whether progress is undetermined is
 * decided by a lookup that can still be running when the page first paints.
 */

export enum WeeklyCheckupStep {
  Intro = 1,
  ProgressReview = 2,
  BodyfatEstimation = 3,
  NutritionLogging = 4,
  CalorieBaseline = 5,
  ProgramUpdate = 6
}

/** The two conditions that hide steps, as the page sees them right now. */
export interface WeeklyCheckupStepConditions {
  /** false when we already have a recent body-fat measurement, so that step is hidden */
  showBodyfatEstimation: boolean;
  /** true when there is not enough history to recommend a calorie change */
  progressUndetermined: boolean;
}

const ALL_STEPS: readonly WeeklyCheckupStep[] = [
  WeeklyCheckupStep.Intro,
  WeeklyCheckupStep.ProgressReview,
  WeeklyCheckupStep.BodyfatEstimation,
  WeeklyCheckupStep.NutritionLogging,
  WeeklyCheckupStep.CalorieBaseline,
  WeeklyCheckupStep.ProgramUpdate
];

function isHidden(step: WeeklyCheckupStep, conditions: WeeklyCheckupStepConditions): boolean {
  // if the body-fat measurement is recent enough, that step is hidden
  if (step === WeeklyCheckupStep.BodyfatEstimation) return !conditions.showBodyfatEstimation;
  // if progress is undetermined, there is nothing to log against and no baseline to set
  if (step === WeeklyCheckupStep.NutritionLogging || step === WeeklyCheckupStep.CalorieBaseline) {
    return conditions.progressUndetermined;
  }
  return false;
}

/**
 * The steps this person walks, in order.
 *
 * `currentStep` is always included even when the conditions would hide it. That
 * only matters in one situation: the conditions can settle while the person is
 * already past the step they affect, and a list that dropped the step somebody is
 * standing on would make the counter read a number outside its own total. Keeping
 * it means the counter always sits between 1 and the total it advertises.
 */
export function getVisibleSteps(
  currentStep: WeeklyCheckupStep,
  conditions: WeeklyCheckupStepConditions
): WeeklyCheckupStep[] {
  return ALL_STEPS.filter(step => step === currentStep || !isHidden(step, conditions));
}

/** The current step's place in the list, counting from 1. What the "Step N of M" label shows as N. */
export function visibleStepNumber(
  currentStep: WeeklyCheckupStep,
  conditions: WeeklyCheckupStepConditions
): number {
  return getVisibleSteps(currentStep, conditions).indexOf(currentStep) + 1;
}

/** How many steps this person walks. What the "Step N of M" label shows as M. */
export function visibleStepCount(
  currentStep: WeeklyCheckupStep,
  conditions: WeeklyCheckupStepConditions
): number {
  return getVisibleSteps(currentStep, conditions).length;
}

/** Where Next goes, or undefined when the person is already on the last step they walk. */
export function nextVisibleStep(
  currentStep: WeeklyCheckupStep,
  conditions: WeeklyCheckupStepConditions
): WeeklyCheckupStep | undefined {
  const steps = getVisibleSteps(currentStep, conditions);
  return steps[steps.indexOf(currentStep) + 1];
}

/** Where Back goes, or undefined when the person is already on the first step they walk. */
export function previousVisibleStep(
  currentStep: WeeklyCheckupStep,
  conditions: WeeklyCheckupStepConditions
): WeeklyCheckupStep | undefined {
  const steps = getVisibleSteps(currentStep, conditions);
  const index = steps.indexOf(currentStep);
  // if the current step is the first one, there is nothing behind it
  if (index <= 0) return undefined;
  return steps[index - 1];
}

/** what decides whether the check-up loads the Body Composition overview (#5456) */
export interface CheckupOverviewConditions {
  /** the coaching dashboard switch, for the signed-in account */
  coachingDashboardOn: boolean;
  /** this page open is the coach's review screen, which has its own layout */
  isCoachReviewScreen: boolean;
  /** the check-up was submitted and its coach has not answered yet */
  isAwaitingCoach: boolean;
}

/**
 * Whether the check-up loads the Body Composition overview. Never while the switch is off, never
 * on the coach's review screen, and never for a check-up that is waiting for its coach: a coached
 * client sees only the "awaiting review" card until the coach has answered.
 */
export function checkupLoadsOverview(conditions: CheckupOverviewConditions): boolean {
  return conditions.coachingDashboardOn && !conditions.isCoachReviewScreen && !conditions.isAwaitingCoach;
}
